import { emailDomain, isFreeMail } from './emailDomains';
import { searchable } from './search';

/**
 * Căsuța agentului (2026-10-28): fiecare firmă are o adresă Procumi
 * (`atlas-<cod>@<domeniu>`). Ofertele ajung acolo în două feluri:
 * - furnizorul răspunde la o cerere trimisă din Procumi (adresa e și ea în „Reply-To”);
 * - omul dă Forward unei oferte primite la el în căsuță.
 * Agentul ghicește furnizorul și cererea după conținut, nu după un cod în subiect
 * (furnizorii scriu des mail nou, vezi „Ce urmează: mailurile” în MEMORY.md),
 * citește prețurile și le lasă în formularul de ofertă: omul confirmă.
 * Aici e partea fără bază de date, ca să se poată testa.
 */

/** Numele agentului, ales de proprietar pe 2026-10-28: în meniu, în notificări, în adresa căsuței. */
export const AGENT_NAME = 'Atlas';
/** cum apare în meniu și ca titlu de pagină: „AI” spune dintr-o privire ce e (proprietarul, 2026-10-28) */
export const AGENT_LABEL = `${AGENT_NAME} AI`;

export const INBOX_PREFIX = 'atlas-';
/** prefixul din prima zi; adresele făcute cu el merg în continuare */
const OLD_PREFIX = 'oferte-';

/** Codul din adresă: litere mici și cifre, ușor de dictat, greu de ghicit. */
export const INBOX_TOKEN_RE = /^[a-z0-9]{10}$/;

export const inboxAddress = (token: string, domain: string): string => `${INBOX_PREFIX}${token}@${domain}`;

/** „Ion Pop <ion@firma.ro>” → { name: 'Ion Pop', email: 'ion@firma.ro' }. */
export function parseAddress(raw: string): { name: string; email: string } | null {
  const m =
    raw.match(/<\s*([^<>\s]+@[^<>\s]+)\s*>/) ?? raw.match(/([^\s<>"'(),;:]+@[^\s<>"'(),;:]+\.[a-z]{2,})/i);
  if (!m) return null;
  const email = m[1]!.toLowerCase().replace(/^mailto:/, '');
  const name = raw.replace(m[0], '').replace(/["']/g, '').trim();
  return { name, email };
}

/** Codul firmei din adresele la care a venit mailul; null = nu e pentru nicio căsuță. */
export function inboxTokenOf(addresses: string[], domain: string): string | null {
  const d = domain.toLowerCase();
  for (const a of addresses) {
    const email = parseAddress(a)?.email;
    if (!email || emailDomain(email) !== d) continue;
    const local = email.slice(0, email.indexOf('@'));
    const prefix = [INBOX_PREFIX, OLD_PREFIX].find((p) => local.startsWith(p));
    if (!prefix) continue;
    const token = local.slice(prefix.length).split('+')[0]!;
    if (INBOX_TOKEN_RE.test(token)) return token;
  }
  return null;
}

/**
 * Expeditorul original dintr-un mail dat Forward: rândul „From:” / „De la:” de sub
 * antetul redirecționării (Gmail, Outlook, Apple Mail, Yahoo, în română și engleză).
 * null = nu e un forward sau nu se vede cine a trimis.
 */
export function forwardedSender(text: string): { name: string; email: string } | null {
  const lines = text.replace(/\r/g, '').split('\n');
  const start = lines.findIndex((l) =>
    /(forwarded message|original message|mesaj (redirec[tț]ionat|original|transmis)|begin forwarded message|weitergeleitete nachricht|^\s*-{3,}\s*$)/i.test(
      l,
    ),
  );
  const from = start >= 0 ? start : 0;
  for (const l of lines.slice(from, from + 40)) {
    const m = l.replace(/^[>\s*]+/, '').match(/^(from|de la|de|von|expeditor)\s*:\s*(.+)$/i);
    if (!m) continue;
    const a = parseAddress(m[2]!);
    if (a) return a;
  }
  return null;
}

export interface InboxSupplier {
  id: string;
  name: string;
  emails: string[];
}

export interface SupplierMatch {
  supplierId: string;
  /** 'email' = adresa e în agendă; 'domain' = același domeniu de firmă */
  by: 'email' | 'domain';
}

/**
 * Furnizorul după adresă: întâi adresa exactă dintr-o persoană de contact, apoi
 * domeniul firmei (nu Gmail sau Yahoo, pe care le au oameni din firme diferite).
 * Două firme pe același domeniu = nesigur, deci null.
 */
export function matchSupplier(emails: string[], suppliers: InboxSupplier[]): SupplierMatch | null {
  const wanted = emails.map((e) => e.toLowerCase());
  for (const e of wanted) {
    const s = suppliers.find((x) => x.emails.some((c) => c.toLowerCase() === e));
    if (s) return { supplierId: s.id, by: 'email' };
  }
  for (const e of wanted) {
    if (isFreeMail(e)) continue;
    const d = emailDomain(e);
    const hits = suppliers.filter((x) => x.emails.some((c) => !isFreeMail(c) && emailDomain(c) === d));
    if (hits.length === 1) return { supplierId: hits[0]!.id, by: 'domain' };
  }
  return null;
}

export interface InboxRfq {
  rfqId: string;
  sendId: string;
  title: string;
  items: string[];
  /** furnizorul a dat deja oferta pe ea */
  hasOffer: boolean;
  /** când a plecat cererea la furnizor; null = nemarcată */
  sentAt: Date | null;
}

const words = (text: string): Set<string> =>
  new Set(
    searchable(text)
      .split(/[^a-z0-9]+/)
      .filter((w) => w.length >= 3 || /\d/.test(w)),
  );

/** Cât din cererea asta se regăsește în mail: cuvintele din titlu și din poziții. */
export function rfqScore(rfq: InboxRfq, text: string): number {
  const inMail = words(text);
  const own = words([rfq.title, ...rfq.items].join(' '));
  if (!own.size) return 0;
  let hit = 0;
  for (const w of own) if (inMail.has(w)) hit += 1;
  return hit / own.size;
}

/**
 * Cererea la care răspunde mailul, dintre cererile deschise trimise furnizorului.
 * Una singură → ea. Mai multe → cea ale cărei cuvinte apar clar mai mult în mail
 * (subiect, text, numele documentelor). Altfel null: omul alege dintr-o listă scurtă.
 */
export function pickRfq(candidates: InboxRfq[], text: string): InboxRfq | null {
  if (!candidates.length) return null;
  if (candidates.length === 1) return candidates[0]!;
  const scored = candidates
    .map((c) => ({ c, s: rfqScore(c, text) + (c.hasOffer ? 0 : 0.05) }))
    .sort((a, b) => b.s - a.s || (b.c.sentAt?.getTime() ?? 0) - (a.c.sentAt?.getTime() ?? 0));
  const [best, next] = scored;
  if (best!.s >= 0.2 && best!.s - next!.s >= 0.1) return best!.c;
  return null;
}

/** Ce poate face omul cu un mail din căsuță. */
export type InboundStatus =
  /** furnizor și cerere găsite, prețurile citite: așteaptă confirmarea omului */
  | 'ready'
  /** furnizorul sau cererea nu s-au putut ghici: omul alege */
  | 'unmatched'
  /** oferta a fost salvată din el */
  | 'done'
  /** omul l-a pus deoparte (nu era o ofertă) */
  | 'ignored';

/** Tipurile de atașamente pe care agentul le citește și le păstrează la ofertă. */
export const INBOUND_FILE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

/**
 * Tipul cu care se păstrează un atașament, sau null dacă nu e de păstrat. Un Excel
 * vine des ca „application/octet-stream”: după nume, e Excel (Ticket 08; și .xls).
 */
export function inboundMime(filename: string, mime: string): string | null {
  if (/\.xlsx$/i.test(filename)) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (/\.xls$/i.test(filename)) return 'application/vnd.ms-excel';
  return INBOUND_FILE_TYPES.has(mime) ? mime : null;
}

/** Mai mare de atât nu se păstrează (ca la documentele urcate de mână). */
export const INBOUND_MAX_FILE = 15 * 1024 * 1024;

/* ---------- ce vede interfața ---------- */

export interface InboxFileInfo {
  index: number;
  filename: string;
  mime: string;
  size: number;
}

export interface InboxMail {
  id: string;
  fromEmail: string;
  fromName: string;
  /** expeditorul ofertei (diferit de `fromEmail` la un Forward) */
  senderEmail: string;
  subject: string;
  /** venit pe mail sau încărcat de furnizor pe linkul lui (Ticket 08) */
  via?: 'email' | 'link';
  /** începutul textului, pentru listă */
  excerpt: string;
  status: InboundStatus;
  note: string;
  receivedAt: string;
  supplier: { id: string; name: string } | null;
  rfq: { id: string; title: string } | null;
  sendId: string | null;
  files: InboxFileInfo[];
  /** câte poziții a citit agentul; null = n-a citit */
  readCount: number | null;
}

export interface InboxList {
  /** firma are Atlas (`agentAvailable`): pachet plătit sau, cu setarea din consolă, probă */
  allowed: boolean;
  /** în probă: câte documente a citit AI-ul și câte are voie (setare din consolă); altfel null */
  trial?: { used: number; cap: number } | null;
  /** căsuța e pornită pe server (INBOUND_DOMAIN) */
  enabled: boolean;
  /** adresa firmei; null când căsuța e oprită */
  address: string | null;
  mails: InboxMail[];
}

/** O cerere deschisă, cu furnizorii ei, ca omul să aleagă unde merge un mail. */
export interface InboxOption {
  rfqId: string;
  title: string;
  sends: { sendId: string; supplierId: string; supplier: string }[];
}

/* ---------- aceeași ofertă, primită de două ori ---------- */

/** Ce compară agentul între două mailuri: amprentele documentelor și textul. */
export interface OfferFingerprint {
  text: string;
  /** sha256 al fiecărui document păstrat */
  hashes: string[];
}

const flat = (t: string): string =>
  searchable(t.replace(/^[ \t]*>+/gm, ' '))
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Aceeași ofertă a venit de două ori: furnizorul a răspuns (și căsuța a primit-o prin
 * Reply-To), iar omul i-a dat și Forward. Același document (aceeași amprentă) sau
 * textul unuia cuprins în celălalt (Forward-ul păstrează mailul original). Mijlocul
 * textului mai scurt, nu începutul: saluturile și antetul Forward-ului diferă.
 */
export function sameOffer(a: OfferFingerprint, b: OfferFingerprint): boolean {
  if (a.hashes.some((h) => b.hashes.includes(h))) return true;
  const [short, long] = [flat(a.text), flat(b.text)].sort((x, y) => x.length - y.length);
  if (short!.length < 80) return false;
  const mid = Math.floor(short!.length / 2);
  const piece = short!.slice(Math.max(0, mid - 75), mid + 75);
  return long!.includes(piece);
}
